import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Trophy, BarChart3, Youtube, FileText, Settings, Vote, Award } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.includes('/api/files/')) {
    const path = url.substring(url.indexOf('/api/files/'));
    return `${BACKEND_URL}${path}`;
  }
  return url;
};

const VotePage = () => {
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [step, setStep] = useState(1);  // 1=pick voter, 2=password, 3=vote, 4=success
  const [selectedVoter, setSelectedVoter] = useState('');
  const [password, setPassword] = useState('');
  const [puskas, setPuskas] = useState('');
  const [mvp, setMvp] = useState('');
  const [muralha, setMuralha] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSession();
    fetchPlayers();
  }, []);

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/sessions/open-for-voting`);
      setSession(res.data?.session);
    } catch (e) { /* ignore */ }
  };

  const fetchPlayers = async () => {
    try {
      const res = await axios.get(`${API}/players`);
      setPlayers(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const confirmedPlayers = session
    ? players.filter(p => session.confirmed_players?.includes(p.name))
    : [];
  const eligible = session?.eligible || { puskas: [], mvp: [], muralha: [] };
  // Voter must have played that day (in MVP eligible list)
  const voters = players.filter(p => eligible.mvp?.includes(p.name));
  const asCards = (names) => players.filter(p => names?.includes(p.name));

  const handleVoterPick = (name) => {
    setSelectedVoter(name);
    setPassword('');
    setErrorMsg('');
    setStep(2);
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      setErrorMsg('Digita a senha');
      return;
    }
    // Verify password by attempting verify-password endpoint
    try {
      await axios.post(`${API}/verify-password`, { player_name: selectedVoter, password });
      setStep(3);
      setErrorMsg('');
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Senha incorreta');
    }
  };

  const handleSubmitVotes = async () => {
    if (!puskas && !mvp && !muralha) {
      setErrorMsg('Selecciona pelo menos uma categoria para votar');
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/sessions/vote-awards`, {
        match_id: session.match_id,
        voter_name: selectedVoter,
        password,
        puskas: puskas || null,
        mvp: mvp || null,
        muralha: muralha || null,
      });
      setStep(4);
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Erro ao registar votos');
    }
    setSubmitting(false);
  };

  const renderPlayerOption = (p, selectedValue, onClick, testId) => (
    <button
      key={p.name}
      type="button"
      onClick={() => onClick(p.name === selectedValue ? '' : p.name)}
      className={`flex items-center gap-3 p-3 border-2 transition-all rounded-lg text-left ${
        p.name === selectedValue
          ? 'bg-yellow-100 border-yellow-500 scale-[0.98]'
          : 'bg-white border-zinc-300 hover:border-yellow-400'
      }`}
      data-testid={testId}
    >
      {p.profile_image_url ? (
        <img src={getImageUrl(p.profile_image_url)} alt={p.name} className="w-10 h-10 rounded-full object-cover border" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center font-bold">{p.name.charAt(0)}</div>
      )}
      <span className="font-bold text-sm sm:text-base">{p.name}</span>
      {p.name === selectedValue && <span className="ml-auto text-yellow-600 font-black">✓</span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="vote-page">
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="text-2xl sm:text-3xl font-black tracking-tighter hover:text-green-400 transition-colors">⚽ DOMINGÃO</a>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin"><Settings size={24} /></a>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <a href="/" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-home">HOME</a>
            <a href="/votar" className="flex-shrink-0 bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold text-sm" data-testid="nav-vote"><Vote className="inline mr-1" size={16} /> PRÉMIOS</a>
            <a href="/estatisticas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-stats"><BarChart3 className="inline mr-1" size={16} /> ESTATÍSTICAS</a>
            <a href="/torneios" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-torneios">
              <Trophy className="inline mr-1" size={16} /> TORNEIOS
            </a>
            <a href="/partidas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-matches"><Calendar className="inline mr-1" size={16} /> PARTIDAS</a>
            <a href="/rankings" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rankings"><Trophy className="inline mr-1" size={16} /> RANKINGS</a>
            <a href="/replays" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-replays"><Youtube className="inline mr-1" size={16} /> REPLAYS</a>
            <a href="/regras" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rules"><FileText className="inline mr-1" size={16} /> REGRAS</a>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight uppercase flex items-center gap-2">
            <Award size={28} className="text-yellow-500" /> PRÉMIOS DA RODADA
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Vota nos melhores momentos da sessão</p>
        </div>

        {!session ? (
          <div className="bg-white rounded-2xl border-2 border-zinc-200 p-12 text-center" data-testid="no-session">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-black mb-3">Sem votação aberta</h2>
            <p className="text-zinc-600 mb-6">O admin abre a votação dos prémios após cada sessão. Volta após o próximo Domingão!</p>
            <a href="/" className="btn-secondary inline-block" data-testid="back-home">← Voltar ao Início</a>
          </div>
        ) : (
          <section className="bg-white rounded-2xl border-2 border-zinc-200 p-6">
            <div className="mb-4 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg text-center">
              <p className="font-bold text-yellow-800">📅 Sessão de {session.date} às {session.time}</p>
              <p className="text-sm text-zinc-600">📍 {session.location_url ? (
                <a href={session.location_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-testid={`vote-location-link-${session.id}`}>{session.location}</a>
              ) : session.location}</p>
            </div>

            {step === 1 && (
              <div data-testid="step-pick-voter">
                <h2 className="text-xl font-black uppercase mb-4">Quem és tu?</h2>
                <p className="text-sm text-zinc-500 mb-4">Apenas jogadores presentes na sessão podem votar.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {voters.map(p => renderPlayerOption(p, '', () => handleVoterPick(p.name), `pick-voter-${p.name}`))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-password">
                <button onClick={() => { setStep(1); setSelectedVoter(''); setPassword(''); setErrorMsg(''); }} className="text-sm underline mb-4">← Voltar</button>
                <h2 className="text-xl font-black uppercase mb-2">Olá, {selectedVoter}!</h2>
                <p className="text-sm text-zinc-500 mb-4">Digita a tua senha para confirmar a tua identidade.</p>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  placeholder="Senha"
                  className="input-field text-2xl text-center tracking-widest mb-3"
                  maxLength={6}
                  autoFocus
                  data-testid="voter-password-input"
                />
                {errorMsg && <p className="text-red-600 text-sm font-bold mb-3">{errorMsg}</p>}
                <button onClick={handlePasswordSubmit} className="btn-primary w-full" data-testid="password-submit-btn">CONFIRMAR</button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6" data-testid="step-vote">
                <button onClick={() => { setStep(1); setSelectedVoter(''); setPassword(''); setPuskas(''); setMvp(''); setMuralha(''); setErrorMsg(''); }} className="text-sm underline">← Trocar de votante</button>

                {/* Puskás do Dia (only players who scored) */}
                <div>
                  <h3 className="text-lg font-black uppercase mb-2 flex items-center gap-2">🥇 Puskás do Dia <span className="text-xs text-zinc-500 font-normal">— Melhor Golo (só quem marcou)</span></h3>
                  {eligible.puskas.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic p-3 bg-zinc-50 rounded">Ninguém marcou golo nesse dia ainda.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {asCards(eligible.puskas).filter(p => p.name !== selectedVoter).map(p => renderPlayerOption(p, puskas, setPuskas, `vote-puskas-${p.name}`))}
                    </div>
                  )}
                </div>

                {/* MVP do Dia (any player who played that day) */}
                <div>
                  <h3 className="text-lg font-black uppercase mb-2 flex items-center gap-2">⭐ MVP do Dia <span className="text-xs text-zinc-500 font-normal">— Jogador do Dia</span></h3>
                  {eligible.mvp.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic p-3 bg-zinc-50 rounded">Sem jogadores presentes.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {asCards(eligible.mvp).filter(p => p.name !== selectedVoter).map(p => renderPlayerOption(p, mvp, setMvp, `vote-mvp-${p.name}`))}
                    </div>
                  )}
                </div>

                {/* Muralha do Dia (only players with saves > 0) */}
                <div>
                  <h3 className="text-lg font-black uppercase mb-2 flex items-center gap-2">🧤 Muralha do Dia <span className="text-xs text-zinc-500 font-normal">— Só quem fez defesas</span></h3>
                  {eligible.muralha.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic p-3 bg-zinc-50 rounded">Nenhuma defesa registada nesse dia.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {asCards(eligible.muralha).filter(p => p.name !== selectedVoter).map(p => renderPlayerOption(p, muralha, setMuralha, `vote-muralha-${p.name}`))}
                    </div>
                  )}
                </div>

                {errorMsg && <p className="text-red-600 text-sm font-bold">{errorMsg}</p>}
                <button onClick={handleSubmitVotes} disabled={submitting} className="btn-primary w-full" data-testid="submit-awards-btn">
                  {submitting ? 'A enviar...' : '✅ ENVIAR VOTOS'}
                </button>
                <p className="text-xs text-zinc-500 text-center">Podes deixar categorias em branco se não tiveres preferência</p>
              </div>
            )}

            {step === 4 && (
              <div className="text-center py-8" data-testid="step-success">
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-2xl font-black mb-2">Votos registados!</h2>
                <p className="text-zinc-600 mb-6">Obrigado por participar. Os vencedores serão anunciados quando o admin encerrar a votação.</p>
                <a href="/" className="btn-secondary inline-block" data-testid="back-home-success">← Voltar ao Início</a>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default VotePage;
